import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";

const ONBOARDING_KEY = "dashboard_onboarding_completed";

export function useDashboardOnboarding() {
  const { $i18n } = useNuxtApp();
  const t = $i18n.t.bind($i18n);

  const hasCompletedOnboarding = (): boolean => {
    try {
      return localStorage.getItem(ONBOARDING_KEY) === "true";
    } catch {
      return false;
    }
  };

  const markOnboardingCompleted = () => {
    try {
      localStorage.setItem(ONBOARDING_KEY, "true");
    } catch {
      // storage unavailable
    }
  };

  const startTour = () => {
    const steps: DriveStep[] = [
      {
        element: "[data-tour='quick-access']",
        popover: {
          title: t("dashboard.onboarding.quick_access_title"),
          description: t("dashboard.onboarding.quick_access_desc"),
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "[data-tour='jobs-profile']",
        popover: {
          title: t("dashboard.onboarding.jobs_profile_title"),
          description: t("dashboard.onboarding.jobs_profile_desc"),
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "[data-tour='add-company']",
        popover: {
          title: t("dashboard.onboarding.add_company_title"),
          description: t("dashboard.onboarding.add_company_desc"),
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "[data-tour='add-game']",
        popover: {
          title: t("dashboard.onboarding.add_game_title"),
          description: t("dashboard.onboarding.add_game_desc"),
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "[data-tour='personalize']",
        popover: {
          title: t("dashboard.onboarding.personalize_title"),
          description: t("dashboard.onboarding.personalize_desc"),
          side: "left",
          align: "start",
        },
      },
      {
        element: "[data-tour='content-feed']",
        popover: {
          title: t("dashboard.onboarding.content_title"),
          description: t("dashboard.onboarding.content_desc"),
          side: "top",
          align: "center",
        },
      },
      {
        element: "[data-tour='sidebar-widgets']",
        popover: {
          title: t("dashboard.onboarding.sidebar_title"),
          description: t("dashboard.onboarding.sidebar_desc"),
          side: "left",
          align: "start",
        },
      },
      {
        element: "[data-tour='platform-switcher'] .sidebar",
        popover: {
          title: t("dashboard.onboarding.platform_switcher_title"),
          description: t("dashboard.onboarding.platform_switcher_desc"),
          side: "left",
          align: "start",
        },
        onHighlightStarted: () => {
          const sidebar = document.querySelector("[data-tour='platform-switcher'] .sidebar") as HTMLElement;
          if (sidebar && !sidebar.classList.contains("open")) {
            const toggler = sidebar.querySelector(".sidebar__toggler") as HTMLElement;
            toggler?.click();
          }
        },
      },
      {
        element: "[data-tour='restart-tour']",
        popover: {
          title: t("dashboard.onboarding.restart_tour_title"),
          description: t("dashboard.onboarding.restart_tour_desc"),
          side: "right",
          align: "end",
        },
        onHighlightStarted: () => {
          const platformSidebar = document.querySelector("[data-tour='platform-switcher'] .sidebar") as HTMLElement;
          if (platformSidebar && platformSidebar.classList.contains("open")) {
            const toggler = platformSidebar.querySelector(".sidebar__toggler") as HTMLElement;
            toggler?.click();
          }
        },
      },
    ];

    const driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: "rgba(0, 0, 0, 0.75)",
      stagePadding: 8,
      stageRadius: 4,
      popoverClass: "mg-onboarding-popover",
      nextBtnText: t("dashboard.onboarding.next"),
      prevBtnText: t("dashboard.onboarding.prev"),
      doneBtnText: t("dashboard.onboarding.done"),
      progressText: "{{current}} / {{total}}",
      steps,
      onHighlighted: (_el, step) => {
        const popover = document.querySelector(".mg-onboarding-popover");
        if (popover) {
          const progressEl = popover.querySelector(".driver-popover-progress-text");
          popover.classList.toggle("mg-onboarding-first-step", progressEl?.textContent?.trim().startsWith("1 ") ?? false);
        }
      },
      onDestroyStarted: () => {
        markOnboardingCompleted();
        driverObj.destroy();
      },
    });

    driverObj.drive();
  };

  const startIfFirstVisit = () => {
    if (!hasCompletedOnboarding()) {
      nextTick(() => {
        setTimeout(() => startTour(), 1000);
      });
    }
  };

  return {
    startTour,
    startIfFirstVisit,
    hasCompletedOnboarding,
    markOnboardingCompleted,
  };
}
